import {useEffect, useState, useRef, useMemo} from "react";

import { AgGridReact } from "ag-grid-react";
import { Autocomplete, Box, TextField, FormControlLabel, Checkbox } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import bomService from "@plannel/services/data/bom-service";
import bomVersionService from "@plannel/services/data/bom-version-service";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import BomVersionModal from "@plannel/components/modal/BomVersionModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, DateRangeButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import bomVersionUtils from "@plannel/utils/bom-version-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import FilterContainer from "@plannel/components/layout/FilterContainer";

const { itemType } = CodeUtil;
const { snackBarMessageColor } = ColorUtil;

const BASE_BOM_VER_ID = 0;
const BOM_ITEM_TYPES = ["FG", "KIT"];

const BomMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ? reduxViewState.filter : {});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const bomGridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [boms, setBoms] = useState([]);
  const [isChildComp, setIsChildComp] = useState(reduxViewState?.isChildComp ?? false);

  const itemTypes = BOM_ITEM_TYPES;
  const [itemTypeSearch, setItemTypeSearch] = useState(reduxViewState?.itemTypeSearch ?? null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters: null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [effPeriod, setEffPeriod] = useState({
    start: reduxViewState?.effStartDt ? new ZDate(reduxViewState.effStartDt) : null,
    end: reduxViewState?.effEndDt ? new ZDate(reduxViewState.effEndDt) : null
  });

  /* 모품목 코드 → 그 모품목으로 BOM 행을 붙일 수 있는 버전 후보. 필요할 때 한 품목씩 받아 캐시한다.
     versionFetchesRef 는 진행 중인 요청을 담아 같은 품목을 중복 조회하지 않게 한다. */
  const versionsByItemCdRef = useRef({});
  const versionFetchesRef = useRef({});
  const [versionItemCd, setVersionItemCd] = useState(null);
  const [openVersionModal, setOpenVersionModal] = useState(false);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemValueFormatter = params => {
    const { value } = params;

    return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
  };

    const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const changeEffPeriod = (type, date) => {
    if (date && isNaN(date.getTime())) {
      return;
    }

    setEffPeriod(prev => ({ ...prev, [type]: date ?? null }));
  };

  const clearEffPeriod = (type, date) => {
    if (!date) {
      setEffPeriod(prev => ({ ...prev, [type]: null }));
    }
  };

  const sameBomVerId = (left, right) => String(left ?? BASE_BOM_VER_ID) === String(right ?? BASE_BOM_VER_ID);
  const isBaseBomRow = data => sameBomVerId(data?.bomVerId, BASE_BOM_VER_ID);
  const versionKey = itemCd => String(itemCd ?? "").trim().toUpperCase();
  const baseBomLabel = () => GridUtils.gridValueL10N("baseBom");

  const getRowBomVersions = data => versionsByItemCdRef.current[versionKey(data?.itemCd)] ?? [];

  /* 후보 라벨 ↔ 버전 짝. 버전 코드는 소유 완제품 안에서만 고유해서(z_bom_version_uk) 한 반제품이 여러
     완제품에 쓰이면 같은 "V1" 이 여러 건 후보로 올라온다. 코드로 되찾으면 먼저 걸린 버전의 id 가
     저장되므로, 소유 완제품을 붙인 라벨로 한 건씩 짝지어 그 모호함을 없앤다.
     라벨은 (소유 완제품, 버전 코드) 조합이라 z_bom_version_uk 에 의해 항상 서로 다르다. */
  const getBomVersionEntries = data => getRowBomVersions(data).map(version => ({
    label: bomVersionUtils.bomVersionLabel(version.bomVerCd, version.itemCd, data?.itemCd),
    version
  }));

  const getBomVersionOptions = data => [baseBomLabel(), ...getBomVersionEntries(data).map(entry => entry.label)];

  /* 셀 값은 버전 코드(bomVerCd)로 두고 소유 완제품은 bomVerItemCd 컬럼이 보여 준다. 그래서 편집기를
     다시 열 때 현재 값이 라벨 목록과 달라 미리 선택되지는 않는다 — 선택 결과는 옆 컬럼에 남아 있다. */
  const setBomVersion = params => {
    const label = params.newValue;

    if (!label || label === baseBomLabel()) {
      params.data.bomVerCd = null;
      params.data.bomVerId = BASE_BOM_VER_ID;
      params.data.bomVerItemCd = null;
      return true;
    }

    const picked = getBomVersionEntries(params.data).find(entry => entry.label === label)?.version;
    if (!picked) {
      return false;
    }

    params.data.bomVerCd = picked.bomVerCd;
    params.data.bomVerId = picked.id;
    params.data.bomVerItemCd = picked.itemCd;
    return true;
  };

  const isNewRow = params => !params.data?.id;
  const isBomVersionEditable = params => isNewRow(params) && getRowBomVersions(params.data).length > 0;

  const columnDefs = [
    { headerName: "bomVersion", field: "bomVerCd", colId: "bomVersion", width: 150, filterType: 'string',
      editable: isBomVersionEditable,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      cellEditorParams: params => ({ values: getBomVersionOptions(params.data) }),
      valueFormatter: params => params.value ? params.value : baseBomLabel(),
      valueSetter: setBomVersion },
    /* 버전을 소유한 완제품. 같은 버전 코드가 완제품마다 따로 있을 수 있어 이 컬럼이 있어야 저장된
       행이 어느 완제품의 버전인지 알 수 있다. 기준 BOM 행은 비어 있다. */
    { headerName: "bomVerItemCd", field: "bomVerItemCd", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType', width: 150, filterType: 'string' },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 280, filterType: 'string' },
    { headerName: "itemType", field: "itemType", type: ["nonEditableColumn"], width: 150, valueFormatter: itemValueFormatter, filterType: 'string', filterOption: Object.keys(itemType).map(key => key.toLowerCase()) },
    { headerName: "componentCd", field: "cmptCd", cellClass: 'stringType', width: 150, filterType: 'string' },
    { headerName: "componentName", field: "cmptName", type: ["nonEditableColumn"], width: 280, filterType: 'string' },
    { headerName: "componentType", field: "cmptType", type: ["nonEditableColumn"], width: 150, valueFormatter: itemValueFormatter, filterType: 'string', filterOption: Object.keys(itemType).map(key => key.toLowerCase()) },
    { headerName: "componentQty", field: "cmptQty", type: ["numberColumn", "rightAligned"], width: 120, cellRenderer: params => params.value, filterType: 'number' },
    { headerName: "descTxt", field: "descTxt", width: 350, filterType: 'string' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 160, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 170, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 160, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 170, filterType: 'string' }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (itemTypeSearch) {
      searchFilters.itemType = itemTypeSearch;
    }

    if (effPeriod?.start) {
      searchFilters.effStartDt = effPeriod.start;
    }

    if (effPeriod?.end) {
      searchFilters.effEndDt = effPeriod.end;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    if (filterValue?.category) {
      searchFilters.hierarchyCd = filterValue.category.hrchyCd;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
      /* 서버가 BOM 버전 조건을 화면 표시값("기준 BOM")과 맞춰 비교하도록 라벨을 함께 보낸다. */
      searchFilters.baseBomLabel = baseBomLabel();
    }

    searchFilters.isChildComp = isChildComp;

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  /* 이 모품목으로 BOM 행을 붙일 수 있는 버전 후보를 받아 캐시한다.
     소유 기준(/api/bom-version)이 아니라 참여 기준(/api/bom-version/applicable)으로 묻는다 —
     반제품은 버전을 소유할 수 없어(z_bom_version 은 완제품·판매 키트만) 소유 기준으로는 후보가 늘
     0 건이고, 그러면 반제품 구성 행을 어느 버전에도 붙일 수 없다.
     includeComponentSide 는 "자품목으로만 참여한 버전" 까지 후보로 넣는다. 등록은 아직 없는 행을
     만드는 일이라, 그 반제품이 상위 버전에서 자품목으로 쓰이는 것만으로도 후보가 되어야 한다. */
  const fetchBomVersions = itemCd => {
    const key = versionKey(itemCd);

    if (!key) {
      return Promise.resolve([]);
    }

    if (versionsByItemCdRef.current[key]) {
      return Promise.resolve(versionsByItemCdRef.current[key]);
    }

    if (!versionFetchesRef.current[key]) {
      versionFetchesRef.current[key] = bomVersionService.getApplicable({ searchFilters: { itemCd, includeComponentSide: true } })
        .then(response => {
          const versions = response.data?.results ?? [];
          versionsByItemCdRef.current[key] = versions;
          return versions;
        })
        .catch(e => {
          debug(e);
          return [];
        })
        .then(versions => {
          delete versionFetchesRef.current[key];
          return versions;
        });
    }

    return versionFetchesRef.current[key];
  };

  const syncVersionItemCd = () => {
    setVersionItemCd(extraItemCodesRef.current?.length > 0 ? null : (itemSearch.current || null));
  };

  const retrieveBom = () => {
    syncVersionItemCd();

    const params = getRequestParams();
    bomService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setBoms(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, advancedFilters, itemTypeSearch, currentPage, pageSize, isChildComp, effStartDt: effPeriod?.start ?? null, effEndDt: effPeriod?.end ?? null });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(bomGridRef.current?.api);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: columnDefs, viewName, gridId: "bomGrid", initState: true });
  }

  const removeBom = () => {
    const bomIds = [];
    bomGridRef.current.api.stopEditing();
    bomGridRef.current.api.getSelectedRows().forEach(
      (data) => {
        bomIds.push(data.id)
      }
    );

    if (bomIds.length > 0) {
      bomService.remove(bomIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveBom();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveBom = () => {
    bomGridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(bomGridRef.current.api);
    for (const data of allStateData) {
      if (data.itemCd === data.cmptCd) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ItemComponentNotSame") });
        return;
      }
    }
    if (allStateData.length > 0) {
      bomService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            const msg = message?.message;

            let errMsg = "";
            if (msg === "MSG_CannotSetItemType") {
              errMsg = t(msg, { type: `[${t(itemType.RM.name)}, ${t(itemType.SFG.name)}]` });
            } else if (msg === "MSG_CannotSetCmptType") {
              errMsg = t(msg, { type: `[${t(itemType.KIT.name)}]` });
            } else {
              errMsg = t(msg);
            }

            setSnackInfo({ open: true, severity: message.type, content: errMsg });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
        })
        .then(() => {
          retrieveBom();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveBom();
    } else if (action === "DELETE") {
      removeBom();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveBom();
    }
  }

  const handleCheckbox = e => {
    setIsChildComp(e.target.checked);
  }

  useEffect(() => {
    if (gridLoading) {
      bomGridRef.current?.api?.setColumnsVisible(["bomVerItemCd", "bomVersion"], true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(bomGridRef.current?.api);
      retrieveBom();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams, isChildComp]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setBoms([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue.category, itemTypeSearch, advancedFilters, effPeriod]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const onCellValueChanged = params => {
    if (params.column.getColId() === "itemCd" && isNewRow(params)) {
      /* 모품목이 정해지면 그 품목의 후보 버전을 받아 온다. ag-grid 는 editable·cellEditorParams 를
         동기로 평가하므로, 응답이 온 뒤 셀을 새로 그려야 isBomVersionEditable 이 새 후보로 다시
         평가되고 편집기에 후보가 채워진다.
         이미 고른 버전이 새 모품목의 후보에 없으면 기준 BOM 으로 되돌린다. */
      fetchBomVersions(params.data?.itemCd).then(versions => {
        const kept = isBaseBomRow(params.data)
          || versions.some(version => sameBomVerId(version.id, params.data.bomVerId));

        if (!kept) {
          params.data.bomVerCd = null;
          params.data.bomVerId = BASE_BOM_VER_ID;
          params.data.bomVerItemCd = null;
        }

        params.api.refreshCells({ rowNodes: [params.node], columns: ["bomVerItemCd", "bomVersion"], force: true });
      });
    }

    defaultGridMemo.onCellValueChanged(params);
  }

  const addRow = () => {
    const newRow = {
      id: null,
      bomVerId: BASE_BOM_VER_ID,
      bomVerCd: null,
      bomVerItemCd: null,
      itemCd: null,
      itemName: null,
      itemType: null,
      cmptCd: null,
      cmptName: null,
      cmptQty: null,
      descTxt: null,
      activeFlg: true,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(bomGridRef.current.api, newRow));
  }

  const closeVersionModal = (changed = false) => {
    setOpenVersionModal(false);

    if (changed) {
      /* 버전이 추가·수정·삭제되었으니 품목별 후보 캐시를 버린다. 다음 편집 때 다시 받아 온다. */
      versionsByItemCdRef.current = {};
      initPageToRetrieve();
    }
  }

  return (
    <>
      <FilterContainer>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
          <Autocomplete
            options={itemTypes}
            getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
            value={itemTypeSearch}
            onChange={(_, newValue) => setItemTypeSearch(newValue)}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            sx={{ mr: 1, minWidth: 200 }}
            renderInput={(params) => (
              <TextField {...params} label={t("itemType")} />
            )}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("effStartDt")}
              format="yyyy-MM-dd"
              value={effPeriod.start}
              maxDate={effPeriod.end ?? undefined}
              onAccept={date => changeEffPeriod("start", date)}
              onChange={date => clearEffPeriod("start", date)}
              slotProps={{
                field: { clearable: true },
                textField: { size: "small", sx: { mr: 1, width: 210 } },
              }}
            />
            <DatePicker
              label={t("effEndDt")}
              format="yyyy-MM-dd"
              value={effPeriod.end}
              minDate={effPeriod.start ?? undefined}
              onAccept={date => changeEffPeriod("end", date)}
              onChange={date => clearEffPeriod("end", date)}
              slotProps={{
                field: { clearable: true },
                textField: { size: "small", sx: { mr: 1, width: 210 } },
              }}
            />
          </LocalizationProvider>
          <FormControlLabel
            control={<Checkbox color="primary" name={"childComponent"} checked={isChildComp} onChange={handleCheckbox} />}
            label={t("childComponent")}
            key="childComponent"
            sx={{ display: "inline-flex", minWidth: 110, ml: 1.5 }}
          />
        </Box>
        <Box sx={{ ml: "auto", mr: 1.5 }}>
          <ExcelExportButton title={title} service={bomService} params={getRequestParams()} columnDefs={columnDefs} />
          <DateRangeButton label="bomVersionMgmt" onClick={() => setOpenVersionModal(true)} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="bom-master-grid" className="ag-theme-balham grid-area" sx={{ position: 'relative', height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', mb: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
              <AgGridReact
                  ref={bomGridRef}
                  rowData={boms}
                  {...defaultGridMemo}
                  rowSelection={{
                    ...defaultGridMemo.rowSelection,
                    headerCheckbox: true,
                    checkboxes: true,
                  }}
                  onCellValueChanged={onCellValueChanged}
                  onGridReady={onGridReady}
                  onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
              />
          </Box>
          {openFilter && (
            <Box sx={{ width: '30%', maxHeight: '100%', overflowY: 'auto', ml: 1, border: 1, borderColor: "#cccccc" }}>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
              />
            </Box>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      {openVersionModal && (
        <BomVersionModal
          open={openVersionModal}
          itemCd={versionItemCd}
          onClose={closeVersionModal}
        />
      )}
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(BomMaster);
